/**
 * 发送重置密码链接页面
 */

import { zodResolver } from "@hookform/resolvers/zod";
import { CheckCircle, GalleryVerticalEnd } from "lucide-react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { toast } from "sonner";
import { z } from "zod";

import { useResetPasswordEmailMutation } from "@/modules/auth";
import { ROUTE_PATHS } from "@/shared/constants/route-paths";
import { cn } from "@/shared/lib/utils";
import { Alert, AlertDescription } from "@/shared/ui/alert";
import { Button } from "@/shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/ui/card";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/shared/ui/field";
import { Input } from "@/shared/ui/input";
import { Spinner } from "@/shared/ui/spinner";

const SendResetLinkPage = () => {
  const { t } = useTranslation("reset-password");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const resetPasswordEmailMutation = useResetPasswordEmailMutation();

  const formSchema = z.object({
    email: z.string().email({
      message: t("validation.email"),
    }),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  function onSubmit(values: z.infer<typeof formSchema>) {
    resetPasswordEmailMutation.mutate(
      {
        email: values.email,
      },
      {
        onSuccess: () => {
          toast.success(t("sendSuccess"));
          setIsSubmitted(true);
        },
      }
    );
  }

  const resendEmail = () => {
    resetPasswordEmailMutation.mutate(
      {
        email: form.getValues("email"),
      },
      {
        onSuccess: () => {
          toast.success(t("sendSuccess"));
        },
      }
    );
  };

  return (
    <div className="flex w-full max-w-md flex-col gap-6">
        <a href={ROUTE_PATHS.HOME} className="flex items-center gap-2 self-center font-medium">
          <div className="bg-primary text-primary-foreground flex size-6 items-center justify-center rounded-md">
            <GalleryVerticalEnd className="size-4" />
          </div>
          Pinbox
        </a>
        <div className={cn("flex flex-col gap-6")}>
          {!isSubmitted ? (
            <Card>
              <CardHeader className="px-8 text-center">
                <CardTitle className="text-xl">{t("title")}</CardTitle>
                <CardDescription>{t("description")}</CardDescription>
              </CardHeader>
              <CardContent className="px-8">
                <form onSubmit={form.handleSubmit(onSubmit)}>
                  <FieldGroup>
                    <Controller
                      name="email"
                      control={form.control}
                      render={({ field, fieldState }) => (
                        <Field data-invalid={fieldState.invalid}>
                          <FieldLabel htmlFor="email">
                            {t("emailLabel")}
                          </FieldLabel>
                          <Input
                            {...field}
                            className="h-11"
                            aria-invalid={fieldState.invalid}
                            id="email"
                            type="email"
                            placeholder={t("emailPlaceholder")}
                            autoComplete="off"
                          />
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      )}
                    />

                    <Field>
                      <Button
                        type="submit"
                        className="h-11"
                        disabled={resetPasswordEmailMutation.isPending}
                      >
                        {resetPasswordEmailMutation.isPending && <Spinner />}
                        {resetPasswordEmailMutation.isPending ? t("sending") : t("submitButton")}
                      </Button>
                      <FieldDescription className="text-center">
                        {t("noAccount")}{" "}
                        <Link className="font-medium" to={ROUTE_PATHS.LOGIN}>
                          {" "}
                          {t("login")}
                        </Link>
                      </FieldDescription>
                    </Field>
                  </FieldGroup>
                </form>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader className="px-8 text-center">
                <div className="flex justify-center mb-6">
                  <div className="relative">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                      <CheckCircle className="h-8 w-8 text-green-600" />
                    </div>
                    <div className="absolute inset-0 w-16 h-16 bg-green-200 rounded-full animate-ping opacity-20"></div>
                  </div>
                </div>
                <CardTitle className="text-xl">
                  {t("sendSucces.title")}
                </CardTitle>
                <CardDescription>
                  {t("sendSucces.description", {
                    email: form.getValues("email"),
                  })}
                </CardDescription>
              </CardHeader>
              <CardContent className="px-8">
                <Alert className="mb-7">
                  <AlertDescription>{t("sendSucces.tips")}</AlertDescription>
                </Alert>
                <Field>
                  <Button
                    className="h-11"
                    disabled={resetPasswordEmailMutation.isPending}
                    onClick={resendEmail}
                  >
                    {resetPasswordEmailMutation.isPending && <Spinner />}
                    {resetPasswordEmailMutation.isPending ? t("sending") : t("sendSucces.submitButton")}
                  </Button>
                  <FieldDescription className="text-center">
                    {t("noAccount")}{" "}
                    <Link className="font-medium" to={ROUTE_PATHS.LOGIN}>
                      {" "}
                      {t("login")}
                    </Link>
                  </FieldDescription>
                </Field>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
  );
};

export default SendResetLinkPage;
